import React from "react";
import {Link, useNavigate, useParams} from "react-router-dom";
import {
  Box,
  Button,
  Link as Linked,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material";
import MWCaption from "../../components/MWCaption";
import {FormContents} from "../../styledComponents/StyledCreateForm";
import {StyledDetailContainer, StyledDetailTable} from "../../styledComponents/StyledDetail";
import MWProgress from "../../components/MWProgress";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {levelSelector, tokenSelector, userState} from "../../Recoil/Atom/authState";
import {useGetItemQuery, useRemoveMutation} from "../../hooks/useQueryHooks";
import * as postApi from "../../api/boardApi/postAip";
import * as Params from "../../models/clientModels/interfaces/apiParams";
import {ResponseItem, ResponseRemove} from "../../models/clientModels/interfaces/Response";
import {KPPost} from "../../models/serverModels/board/KPPost";
import AllInboxIcon from "@mui/icons-material/AllInbox";
import {useQueryClient} from "react-query";
import {alertStateAtom} from "../../Recoil/Atom/AlertAtom";

const PostRead = () => {
  let {id} = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const tokenState = useRecoilValue(tokenSelector);
  const userLevel = useRecoilValue(levelSelector);

  const setAlertState = useSetRecoilState(alertStateAtom);

  const isMobile = useMediaQuery("(min-width: 960px)");

  const {
    data: post,
    isLoading,
    isFetching,
  } = useGetItemQuery<ResponseItem<KPPost>>({
    key: ["Post", id],
    api: () => postApi.getItem({id: parseInt(id!), token: tokenState}),
  });

  const {mutate: removeMutate} = useRemoveMutation<ResponseRemove, Params.Remove>({
    api: postApi.remove,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries("getMainBoardList");
        queryClient.invalidateQueries("getBoardWithPosts");
        navigate(`/Home/Dashboard`);
      },
    },
  });

  return (
    <StyledDetailContainer>
      <MWCaption title={"게시글 보기"} color="#01579B" icon={<AllInboxIcon />} />
      <FormContents container rowSpacing={4}>
        <StyledDetailTable>
          {isLoading && isFetching ? (
            <MWProgress />
          ) : (
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell component="th" width="10%">
                    제목
                  </TableCell>
                  <TableCell component="td">{post?.item?.title}</TableCell>
                  <TableCell component="th" width="10%">
                    작성일자
                  </TableCell>
                  <TableCell component="td" width="23.33%">
                    {new Date(post?.item?.entryDate!).getFullYear()}년
                    {new Date(post?.item?.entryDate!).getMonth() + 1}월
                    {new Date(post?.item?.entryDate!).getDate()}일
                  </TableCell>
                  <TableCell component="th" width="10%">
                    외부링크
                  </TableCell>
                  <TableCell component="td" width="23.33%">
                    <a href={post?.item?.linkUrl} target="_blank">
                      {post?.item?.linkName ? post.item.linkName : post?.item?.linkUrl}
                    </a>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th">작성자</TableCell>
                  <TableCell component="td">{post?.item?.writerName}</TableCell>
                  <TableCell component="th" width="10%">
                    조회수
                  </TableCell>
                  <TableCell component="td" width="23.33%">
                    {post?.item?.readCount}
                  </TableCell>
                  <TableCell component="th">첨부파일</TableCell>
                  <TableCell component="td">
                    <Linked href={`/API/postAttachment?token=${tokenState}&id=${post?.item?.uid}`}>
                      {post?.item?.attachmentFile}
                    </Linked>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="td" colSpan={6}>
                    <Typography sx={{whiteSpace: "pre-line"}}>{post?.item?.contents}</Typography>
                  </TableCell>
                </TableRow>
                {post?.item?.imageFile && (
                  <TableRow>
                    <TableCell component="td" colSpan={6}>
                      <img
                        src={`/API/PostImage?token=${tokenState}&id=${post?.item?.uid}`}
                        alt="게시글 첨부 이미지"
                        style={{width: "500px", height: "auto", display: "block", margin: "auto"}}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </StyledDetailTable>
      </FormContents>
      <Box display="flex" justifyContent="center" margin="36px">
        <Button
          variant="contained"
          color="warning"
          size="small"
          onClick={() => navigate(-1)}
          sx={{marginRight: "8px"}}
        >
          뒤로가기
        </Button>
        {userLevel === 5 && (
          <Button
            variant="contained"
            color="secondary"
            size="small"
            sx={{marginRight: "8px"}}
            onClick={() => {
              setAlertState({
                toggle: true,
                message: "등록된 게시글을 삭제하시겠습니까?",
                execution: () => removeMutate({token: tokenState, id: post?.item?.uid!}),
              });
            }}
          >
            삭제
          </Button>
        )}
        {userLevel === 5 && (
          <Button
            variant="contained"
            color="success"
            size="small"
            onClick={() => navigate(`/Post/Edit/${id}/${post?.item?.boardName}`)}
          >
            수정
          </Button>
        )}
      </Box>
    </StyledDetailContainer>
  );
};

export default PostRead;
